import DashboardContentHeading from '../DashboardContentHeading/DashboardContentHeading';
import classes from './AuctionDash.module.scss';
import AuctionDashCard from './AuctionDashCard/AuctionDashCard';
import AuctionDashMenu from './AuctionDashMenu/AuctionDashMenu';
import { Link } from "react-router-dom";
import { useState, useEffect, useCallback } from 'react';
import { carSearch, updateCarFavourite } from '../../../services/index';
import { useContext } from 'react';
import AuthContext from '../../../store/auth-context';
import AuctionDashSkeleton from './AuctionDashSkeleton';
import { useDisclosure } from '@chakra-ui/react';
import UpgradeModal from '../../UI/UpgradeModal';
import { SEARCH_MAX_LENGTH } from '../../../config/constants';

const AuctionDash = () => {
    const [page, setPage] = useState(1);
    const [carsData, setCarsData] = useState([]);
    const [totalCarsToShow] = useState(4);
    const [totalCars, setTotalCars] = useState(0);
    const { userData, isFreeTier } = useContext(AuthContext);
    const [savedFilters, setSavedFilters] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState("None");
    const [filterString, setFilterString] = useState("");
    const [initialLoading, setInitialLoading] = useState(true);
    const [paginationLoading, setPaginationLoading] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();

    useEffect(() => {
        setSavedFilters(userData?.savedFilters);
    }, [userData]);

    useEffect(() => {
        if (selectedFilter && selectedFilter !== "None") {
            const filter = savedFilters?.find(filter => filter.name === selectedFilter);
            if (filter) {
                // Saved searches can carry search-bar text as well as filters
                const search = filter.search ? `&search=${encodeURIComponent(filter.search.slice(0, SEARCH_MAX_LENGTH))}` : "";
                setFilterString((filter.filter || "") + search);
                setPage(1);
            }
        } else {
            setFilterString("");
            setPage(1);
        }
    }, [selectedFilter, savedFilters]);

    const getCarsList = useCallback(() => {
        if (initialLoading || paginationLoading) {
            // Don't need to set loading state here since it's not used
        }
        
        // The backend API might be expecting different parameter formats
        // Let's try without any delay parameter at first for free tier users
        const payload = `pageNo=${page}&size=${totalCarsToShow}${filterString}`;
        
        carSearch(payload)
            .then((res) => {
                setInitialLoading(false);
                setPaginationLoading(false);
                if (res?.message === "Success") {
                    setTotalCars(res?.data[0]?.total || 0);
                    setCarsData(res?.data[0].data);
                } else {
                    console.error("Error fetching car data:", res?.message);
                    setCarsData([]);
                    setTotalCars(0);
                }
            })
            .catch(error => {
                console.error("Error in car search:", error);
                setInitialLoading(false);
                setPaginationLoading(false);
                setCarsData([]);
                setTotalCars(0);
            });
    }, [page, filterString, initialLoading, paginationLoading, totalCarsToShow]);

    useEffect(() => {
        getCarsList();
    }, [getCarsList]);

    useEffect(() => {
        setInitialLoading(true);
    }, [filterString]);

    const nextHandler = () => {
        setPaginationLoading(true);
        setPage(page + 1);
    }

    const prevHandler = () => {
        setPaginationLoading(true);
        setPage(page - 1);
    }

    const favHandler = (content) => {
        updateCarFavourite({ carId: content?._id }).then((res) => {
            if (res.status === 201) {
                setCarsData(prevCars => 
                    prevCars.map(car => 
                        car._id === content._id 
                            ? {...car, isFavorite: !car.isFavorite}
                            : car
                    )
                );
            }
        });
    }

    const setFilter = (filter) => {
        setSelectedFilter(filter === "None" ? "" : filter);
    }
    
    console.log("Selected Filter",selectedFilter);

    return (
        <div className={classes.AuctionDash}>
            <div className={classes.AuctionHeader}>
                <DashboardContentHeading iconText='auction'/>
                <Link to="/auction">See all cars</Link>
            </div>
            <div className={classes.AuctionContentContainer}>
                <AuctionDashMenu savedFilters={savedFilters} setFilter={setFilter}/>
                <div className={classes.AuctionCardsContainer}>
                    {(initialLoading || paginationLoading) ? (
                        [...Array(4)].map((_, index) => (
                            <AuctionDashSkeleton key={index} />
                        ))
                    ) : carsData.length > 0 ? (
                        carsData.map((content, index) => (
                            <AuctionDashCard 
                                key={index} 
                                content={content} 
                                favHandler={favHandler} 
                                isFreeTier={isFreeTier}
                                onUpgradeClick={onOpen}
                            />
                        ))
                    ) : (
                        <div className={classes.noResults}>
                            <p>No cars found with the current filters.</p>
                        </div>
                    )}
                </div>
                <div className={classes.paginationContainer}>
                    <div className={classes.pageText}>
                        Page {page} of {Math.ceil(totalCars/totalCarsToShow) || 1}
                    </div>
                    <div className={classes.paginationButtons}>
                        <button 
                            onClick={prevHandler} 
                            disabled={page === 1 || paginationLoading}
                        >
                            Previous
                        </button>
                        <button 
                            onClick={nextHandler} 
                            disabled={page === Math.ceil(totalCars/totalCarsToShow) || totalCars === 0 || paginationLoading}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
            <UpgradeModal isOpen={isOpen} onClose={onClose} />
        </div>
    );
};

export default AuctionDash;